import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/auth-context";
import { useBrand } from "@/contexts/brand-context";
import { pb } from "@/lib/pocketbase";
import { UpgradeDialog } from "@/components/common/UpgradeDialog";
import { OnboardingStepper } from "@/components/onboarding/OnboardingStepper";
import { WebsiteAnalysisStep } from "@/components/onboarding/WebsiteAnalysisStep";
import { BrandInformationStep } from "@/components/onboarding/BrandInformationStep";
import { ProductsServicesStep } from "@/components/onboarding/ProductsServicesStep";
import { CompletionStep } from "@/components/onboarding/CompletionStep";
import { Brand, defaultOnboardingData } from "@/types/brand";
import {
  Brands,
  BrandsStatusOptions,
  BrandsToneOptions,
} from "@/types/pocketbase-types";
import { toast } from "sonner";
import { useSubscription } from "@/contexts/subscription-context";

const STEPS = ["Website", "Brand Info", "Products", "Complete"];

const ONBOARDING_KEY = "obstep";

export default function Onboarding() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { brands, currentBrand, setCurrentBrand, refreshBrands } = useBrand();
  const { isFreePlan, isTrialUser, subscription } = useSubscription();
  const [currentStep, setCurrentStep] = useState(0);
  const [onboardingData, setOnboardingData] = useState<Brand>(
    defaultOnboardingData,
  );
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");

  // Subscribe to brand updates to keep onboardingData in sync
  useEffect(() => {
    if (currentBrand?.id) {
      console.log(`Setting up subscription for brand: ${currentBrand.id}`);

      // Subscribe to brand updates
      pb.collection("brands").subscribe<Brands>(currentBrand.id, (e) => {
        if (e.action === "update") {
          // Update the brand in the context
          setCurrentBrand(e.record);

          // Update onboardingData with the new brand data
          setOnboardingData((prevData) => ({
            ...prevData,
            name: e.record.name || prevData.name,
            logo: e.record.logo || prevData.logo,
            url: e.record.url || prevData.url,
            description: e.record.description || prevData.description,
            uniqueValue: e.record.uniqueValue || prevData.uniqueValue,
            additionalNotes:
              e.record.additionalNotes || prevData.additionalNotes,
            targetAudience: e.record.targetAudience || prevData.targetAudience,
            tone: e.record.tone || prevData.tone,
            primaryColor: e.record.primaryColor || prevData.primaryColor,
            secondaryColor: e.record.secondaryColor || prevData.secondaryColor,
          }));
        }
      });

      // Cleanup subscription when component unmounts or brand changes
      return () => {
        console.log(`Unsubscribing from brand: ${currentBrand.id}`);
        pb.collection("brands").unsubscribe(currentBrand.id);
      };
    }
  }, [currentBrand?.id, setCurrentBrand]);

  const handleNext = (data: Partial<Brand>) => {
    setOnboardingData((prev) => ({ ...prev, ...data }));
    setCurrentStep((prev) => {
      const newStep = prev + 1;
      localStorage.setItem(
        `${ONBOARDING_KEY}-${currentBrand?.id}`,
        newStep.toString(),
      );
      return newStep;
    });
  };

  const handleBack = () => {
    setCurrentStep((prev) => {
      const newStep = prev - 1;
      localStorage.setItem(
        `${ONBOARDING_KEY}-${currentBrand?.id}`,
        newStep.toString(),
      );
      return newStep;
    });
  };

  // On page load, check the current onboarding step and navigate to that position
  useEffect(() => {
    if (!currentBrand) return;
    const storedStep = localStorage.getItem(
      `${ONBOARDING_KEY}-${currentBrand.id}`,
    );
    if (storedStep) {
      const step = parseInt(storedStep);
      if (step === STEPS.length) {
        navigate("/");
      } else {
        setCurrentStep(step);
        setOnboardingData((prev: Brand) => ({
          ...prev,
          ...(currentBrand as Brand),
        }));
      }
    }
  }, [currentBrand, navigate]);

  const handleWebsiteAnalysis = async (data: Partial<Brand>) => {
    try {
      // Check if user has subscription or trial access
      const hasAccess = subscription || isTrialUser;

      if (!hasAccess) {
        // No subscription and no trial - redirect to pricing
        navigate("/pricing");
        return;
      }

      // Check if trial is expired
      if (isTrialUser && subscription?.trial?.isExpired) {
        setShowUpgradeDialog(true);
        return;
      }

      // Legacy check for free plan with multiple brands
      if (isFreePlan && brands.length >= 1) {
        setShowUpgradeDialog(true);
        return;
      }

      await createBrand(data);
    } catch (error) {
      console.error("Error analyzing website:", error);
      setIsAnalyzing(false);
      toast.error(
        (error as Error).message ||
          "Something went wrong while creating your brand",
        {
          position: "bottom-right",
          duration: 5000,
        },
      );
    }
  };

  const createBrand = async (data: Partial<Brand>) => {
    setIsAnalyzing(true);
    setOnboardingData((prev: Brand) => ({ ...prev, ...data }));
    setLoadingMessage("Fetching brand info...");

    // Create the brand record
    const brandData = {
      name: data.name,
      url: data.url,
      status: BrandsStatusOptions.Active,
      tone: BrandsToneOptions.Professional,
      logo: "", // Default empty values for optional fields
      primaryColor: "",
      secondaryColor: "",
      analyticsId: "",
      analyticsViewId: "",
      searchConsoleVerified: false,
      syncStatus: null,
      org: user?.org,
      language: "English", // Default language
      location: "", // Will be populated from website info if available
      contentObjectives: {
        topics: [],
        keywords: [],
        goals: [],
      },
      products: [],
      competitors: [],
    };

    // Create the brand in PocketBase
    const createdBrand = await pb
      .collection("brands")
      .create<Brands>(brandData);

    // Store the current onboarding step in localstorage for the brand
    localStorage.setItem(`${ONBOARDING_KEY}-${createdBrand.id}`, "1");

    // After 1 second fetch brand data from pocketbase
    await new Promise((resolve) => setTimeout(resolve, 1000));
    const brand = await pb.collection("brands").getOne<Brands>(createdBrand.id);
    setCurrentBrand(brand);
    setOnboardingData((prev) => ({ ...prev, ...(brand as Brand) }));

    // Refresh brands to ensure the context is up to date
    await refreshBrands();

    setIsAnalyzing(false);
    setCurrentStep((prev) => prev + 1);
  };

  const handleComplete = async () => {
    try {
      setIsSubmitting(true);
      setLoadingMessage("Updating your brand profile...");

      if (!currentBrand) {
        return;
      }

      // Update the current brand record
      const brandData = {
        name: onboardingData.name,
        url: onboardingData.url,
        status: BrandsStatusOptions.Active,
        tone: onboardingData.tone,
        logo: onboardingData.logo || "",
        primaryColor: onboardingData.primaryColor,
        secondaryColor: onboardingData.secondaryColor,
        description: onboardingData.description,
        analyticsId: "",
        analyticsViewId: "",
        searchConsoleVerified: false,
        syncStatus: null,
        org: user?.org,
        targetAudience: onboardingData.targetAudience,
        contentObjectives: {
          goals: onboardingData.contentObjectives.goals,
        },
        visualStyle: onboardingData.visualStyle,
        uniqueValue: onboardingData.uniqueValue,
        additionalNotes: onboardingData.additionalNotes,
        products: onboardingData.products,
        competitors: [],
        language: onboardingData.language || "English",
        location: onboardingData.location || "",
      };

      // Update the brand in PocketBase
      const updatedBrand = await pb
        .collection("brands")
        .update<Brands>(currentBrand.id, brandData);

      setCurrentBrand(updatedBrand);

      // Refresh brands to ensure the context is up to date
      await refreshBrands();

      localStorage.removeItem(`${ONBOARDING_KEY}-${updatedBrand.id}`);

      setIsSubmitting(false);
      navigate("/articles/new?onboarding=true");
    } catch (error) {
      console.error("Error creating brand:", error);
      setIsSubmitting(false);
      // Show error message at the top of the form
      toast.error(
        (error as Error).message ||
          "Something went wrong while creating your brand",
        {
          position: "bottom-right",
          duration: 5000,
        },
      );
    }
  };

  // Custom loading screen component
  const renderLoadingScreen = () => (
    <div className="fixed inset-0 flex items-center justify-center bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm z-50">
      <div className="flex flex-col items-center">
        <div className="relative w-16 h-16 mb-4">
          <div className="absolute top-0 left-0 w-full h-full rounded-full border-4 border-t-orange-500 border-r-red-500 border-b-pink-500 border-l-transparent animate-spin"></div>
        </div>
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
          Setting up your brand
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
          {loadingMessage}
        </p>
      </div>
    </div>
  );

  return (
    <>
      <div className="flex flex-col items-center justify-center bg-background p-4 min-h-[calc(100vh-4rem)]">
        {isSubmitting && renderLoadingScreen()}
        <div className="fixed top-0 left-0 right-0 bg-background border-b z-10 py-2 px-4">
          <div className="w-full max-w-3xl mx-auto">
            <OnboardingStepper steps={STEPS} currentStep={currentStep} />
          </div>
        </div>
        <div className="mt-16">
          {currentStep === 0 && (
            <WebsiteAnalysisStep
              data={onboardingData}
              onNext={handleWebsiteAnalysis}
              isAnalyzing={isAnalyzing}
            />
          )}

          {currentStep === 1 && (
            <BrandInformationStep data={onboardingData} onNext={handleNext} />
          )}

          {currentStep === 2 && (
            <ProductsServicesStep
              data={onboardingData}
              onNext={handleNext}
              onBack={handleBack}
            />
          )}

          {currentStep === 3 && (
            <CompletionStep
              data={onboardingData}
              onComplete={handleComplete}
              isSubmitting={isSubmitting}
            />
          )}
        </div>
      </div>
      {showUpgradeDialog && (
        <UpgradeDialog
          open={showUpgradeDialog}
          onOpenChange={(open) => {
            setShowUpgradeDialog(open);
          }}
        />
      )}
    </>
  );
}
